/**
 * Standard matching and phase placement — the ONE place the planner surfaces
 * resolve "which labor standard does this order run, which of its operations,
 * and when does each phase happen".
 *
 * GLOSSARY — two vocabularies meet here, and both are kept on purpose:
 *   - `workstation`  is the API field (orders, operations, assignments, pins).
 *     "work center" and "line" are the words the UI uses for the same thing.
 *   - `phase`        is the API field: one crew line of an operation (a job
 *     role × a head count × a window). "crew line" is the UI word for it.
 *   - `operation`    is a step of the recipe; a work ORDER is one operation
 *     (a "step") planned for a window on a work center.
 *
 * Mirrors the API's LaborStandardResolver and the allocation engine's
 * sequencing (docs/planner/recipe-model.md). If either changes, this has to
 * change with it — otherwise the boards would disagree with the roster they
 * are meant to explain.
 *
 * Model recap: one standard per product (and recipe group); the work center is
 * on each OPERATION, with optional alternative work centers (options). A work
 * order is a STEP: one operation (or every operation on one work center) for a
 * planned window. A batch header has no work center and is never staffed.
 */
import {
	LaborStandard,
	LaborStandardOperation,
	LaborStandardPhase,
} from "../../../services/labor-standards-api";

/** The subset of a work order the matcher reads. Loose so DTOs and rows fit. */
export interface MatchableOrder {
	workstation?: string | null;
	startTime: string;
	endTime?: string | null;
	laborStandardId?: number | null;
	operationId?: number | null;
	productId?: number | null;
	sku?: string | null;
	productItemNumber?: string | null;
}

/** A phase placed on the clock for a specific order. */
export interface PlacedPhase {
	phase: LaborStandardPhase;
	operation: LaborStandardOperation;
	start: Date;
	end: Date;
	/** The work center the phase is staffed on. */
	workstation: string;
}

/**
 * A MACHINE phase: it occupies the unit for its duration but needs nobody.
 * Every surface that reasons about staffing (required hours, staffed state,
 * crew summaries, role legends) must skip these; the timelines still draw
 * them as occupancy so the planner can see why the unit is busy.
 */
export const isMachinePhase = (phase: Pick<LaborStandardPhase, "workersRequired">): boolean =>
	!(Number(phase.workersRequired) > 0);

/** Only the phases that need people. */
export const laborPhases = <T extends Pick<LaborStandardPhase, "workersRequired">>(
	phases: T[] | undefined | null
): T[] => (phases ?? []).filter((phase) => !isMachinePhase(phase));

/**
 * What a machine line is called: the company's own work phase name when the
 * phase is linked to one ("Machine"), else the generic label.
 */
export const machinePhaseLabel = (phase: { workPhaseName?: string | null }): string =>
	(phase.workPhaseName ?? "").trim() || "Machine time";

/**
 * A crew size as entered: "2", "0.5", "2.67" — never "2.00", never rounded
 * up. Fractional crews are real (a half-time operator shared across lines).
 */
export const formatCrew = (workers: number): string => {
	const value = Number(workers) || 0;
	return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
};

const normalise = (value?: string | null): string => (value ?? "").trim().toLowerCase();

/** Work-center equality as the planner sees it: trimmed, case-insensitive. */
export const sameWorkstation = (a?: string | null, b?: string | null): boolean =>
	Boolean(normalise(a)) && normalise(a) === normalise(b);

const addMinutes = (date: Date, minutes: number): Date =>
	new Date(date.getTime() + minutes * 60000);

const sortedOperations = (standard: LaborStandard): LaborStandardOperation[] =>
	[...(standard.operations ?? [])].sort(
		(a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || (a.id ?? 0) - (b.id ?? 0)
	);

/** Every work center an operation can run on: its default plus its options. */
export const operationWorkstations = (operation: LaborStandardOperation): string[] => {
	const names: string[] = [];
	const push = (name?: string | null) => {
		const trimmed = (name ?? "").trim();
		if (trimmed && !names.some((existing) => sameWorkstation(existing, trimmed))) {
			names.push(trimmed);
		}
	};
	push(operation.workstation);
	(operation.workstationOptions ?? []).forEach((option) => push(option.workstation));
	return names;
};

/** True when the operation runs (by default or as an option) on the work center. */
export const operationRunsOn = (
	operation: LaborStandardOperation,
	workstation?: string | null
): boolean => operationWorkstations(operation).some((name) => sameWorkstation(name, workstation));

/**
 * The step's duration when planned on `workstation`: an option's override when
 * the work center is an option with its own duration, else the operation's own
 * duration, else the span of its phases.
 */
export const operationDurationOn = (
	operation: LaborStandardOperation,
	workstation?: string | null
): number => {
	const option = (operation.workstationOptions ?? []).find((candidate) =>
		sameWorkstation(candidate.workstation, workstation)
	);
	if (option?.durationMinutes != null && option.durationMinutes > 0) {
		return option.durationMinutes;
	}
	const phaseSpan = Math.max(
		0,
		...(operation.phases ?? []).map((phase) => phase.startOffsetMinutes + phase.durationMinutes)
	);
	return Math.max(operation.durationMinutes || 0, phaseSpan);
};

/**
 * Every work center a standard has a step on: the operations' own (defaults
 * and options) when the operation tree is loaded, else the server's
 * `workstations` roll-up, else the legacy header. Unique, in first-seen order.
 */
export const standardWorkstations = (standard: LaborStandard): string[] => {
	const fromOperations = (standard.operations ?? []).flatMap(operationWorkstations);
	const names = fromOperations.length > 0 ? fromOperations : standard.workstations ?? [];
	const unique = Array.from(new Set(names.filter(Boolean)));
	if (unique.length > 0) return unique;
	return standard.workstation ? [standard.workstation] : [];
};

/**
 * Does the standard have a step on this work center? Legacy standards whose
 * operations carry no work center fall back to the header `workstation` (and
 * to the server's `workstations` roll-up when the operation tree was not
 * loaded).
 */
export const standardHasStepOn = (
	standard: LaborStandard,
	workstation?: string | null
): boolean => {
	if (!normalise(workstation)) return false;
	const operations = standard.operations ?? [];
	const operationCenters = operations.flatMap(operationWorkstations);
	if (operationCenters.length > 0) {
		return operationCenters.some((name) => sameWorkstation(name, workstation));
	}
	if (standard.workstations && standard.workstations.length > 0) {
		return standard.workstations.some((name) => sameWorkstation(name, workstation));
	}
	return sameWorkstation(standard.workstation, workstation);
};

/**
 * Which standard an order runs. Mirrors the API's LaborStandardResolver:
 * pinned `laborStandardId`, else the product's standard with a step on the
 * order's work center, else the SKU / item number's, else the product's
 * standard regardless of work center.
 */
export const findStandardForOrder = (
	order: MatchableOrder,
	standards: LaborStandard[]
): LaborStandard | undefined => {
	if (order.laborStandardId) {
		const pinned = standards.find((standard) => standard.id === order.laborStandardId);
		if (pinned) return pinned;
	}
	if (order.productId) {
		const byProduct = standards.find(
			(standard) =>
				standard.productId === order.productId && standardHasStepOn(standard, order.workstation)
		);
		if (byProduct) return byProduct;
	}
	const key = (order.sku ?? order.productItemNumber ?? "").trim();
	if (key) {
		const bySku = standards.find(
			(standard) =>
				(standard.sku === key || standard.productItemNumber === key) &&
				standardHasStepOn(standard, order.workstation)
		);
		if (bySku) return bySku;
	}
	if (order.productId) {
		return standards.find((standard) => standard.productId === order.productId);
	}
	return undefined;
};

/**
 * The operations of a standard that an order actually runs: its pinned
 * `operationId`, else every operation on the order's work center, else (legacy
 * standard with no work center on its operations) all of them. A batch header
 * (no work center) runs none. Sorted by sequence.
 */
export const operationsForOrder = (
	order: MatchableOrder,
	standard: LaborStandard
): LaborStandardOperation[] => {
	const operations = sortedOperations(standard);
	if (order.operationId != null) {
		const pinned = operations.find((operation) => operation.id === order.operationId);
		return pinned ? [pinned] : [];
	}
	if (!normalise(order.workstation)) return [];
	const onWorkstation = operations.filter((operation) =>
		operationRunsOn(operation, order.workstation)
	);
	if (onWorkstation.length > 0) return onWorkstation;
	const anyWorkstation = operations.some(
		(operation) => operationWorkstations(operation).length > 0
	);
	return anyWorkstation ? [] : operations;
};

/**
 * Recipe sequencing: operations run one after another in sequence order;
 * operations sharing a `parallelGroup` start together and the cursor advances
 * by the longest member. Returns each operation's start offset (minutes from
 * the order's start).
 */
const recipeOffsets = (
	operations: LaborStandardOperation[],
	durationOf: (operation: LaborStandardOperation) => number
): Map<LaborStandardOperation, number> => {
	const groups: LaborStandardOperation[][] = [];
	const groupIndex = new Map<string, number>();
	operations.forEach((operation) => {
		const key =
			operation.parallelGroup != null ? `g${operation.parallelGroup}` : `op${operation.id}`;
		const existing = groupIndex.get(key);
		if (existing == null) {
			groupIndex.set(key, groups.length);
			groups.push([operation]);
		} else {
			groups[existing].push(operation);
		}
	});

	const offsets = new Map<LaborStandardOperation, number>();
	let cursor = 0;
	groups.forEach((group) => {
		let span = 0;
		group.forEach((operation) => {
			offsets.set(operation, cursor);
			span = Math.max(span, durationOf(operation));
		});
		cursor += span;
	});
	return offsets;
};

/**
 * Where every phase of an order lands on the clock.
 *
 * STEP MODE — the order is exactly one operation (pinned, or the standard has
 * several and only one runs on this work center): the PLANNED window wins. A
 * phase that covers the whole operation (offset 0, duration >= the operation's)
 * stretches or clips to the order's end (or start + duration when no end is
 * set); other phases keep their recipe timing, clipped at the window end.
 *
 * RECIPE MODE — several operations (Peters' setup / production / washup on one
 * line, or a legacy standard): recipe sequencing from the order's start.
 *
 * A batch header has no work center and places nothing.
 */
export const placeOrderPhases = (
	order: MatchableOrder,
	standard: LaborStandard
): PlacedPhase[] => {
	const operations = operationsForOrder(order, standard);
	if (operations.length === 0) return [];

	const orderStart = new Date(order.startTime);
	if (Number.isNaN(orderStart.getTime())) return [];
	const orderEnd = order.endTime ? new Date(order.endTime) : null;
	const fallbackWorkstation = (order.workstation ?? standard.workstation ?? "").trim();

	const totalOperations = (standard.operations ?? []).length;
	const stepMode =
		operations.length === 1 && (order.operationId != null || totalOperations > 1);

	if (stepMode) {
		const operation = operations[0];
		const workstation = operationRunsOn(operation, order.workstation)
			? fallbackWorkstation
			: (operation.workstation ?? fallbackWorkstation).trim();
		const stepDuration = operationDurationOn(operation, workstation);
		// The recipe's own duration for the step; a phase that spans it is the
		// crew that runs the step and follows the window wherever it goes (an
		// option with a longer duration included).
		const nominalDuration = operationDurationOn(operation, operation.workstation);
		const windowEnd =
			orderEnd && orderEnd > orderStart ? orderEnd : addMinutes(orderStart, stepDuration);

		return [...(operation.phases ?? [])]
			.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
			.map((phase) => {
				const coversStep =
					phase.startOffsetMinutes === 0 && phase.durationMinutes >= nominalDuration;
				if (coversStep) {
					return { phase, operation, start: orderStart, end: windowEnd, workstation };
				}
				const start = new Date(
					Math.min(addMinutes(orderStart, phase.startOffsetMinutes).getTime(), windowEnd.getTime())
				);
				const end = new Date(
					Math.min(addMinutes(start, phase.durationMinutes).getTime(), windowEnd.getTime())
				);
				return { phase, operation, start, end, workstation };
			});
	}

	const workstationOf = (operation: LaborStandardOperation): string =>
		operationRunsOn(operation, order.workstation)
			? fallbackWorkstation
			: (operation.workstation ?? fallbackWorkstation).trim();
	const offsets = recipeOffsets(operations, (operation) =>
		operationDurationOn(operation, workstationOf(operation))
	);
	const placed: PlacedPhase[] = [];
	operations.forEach((operation) => {
		const offset = offsets.get(operation) ?? 0;
		const workstation = workstationOf(operation);
		[...(operation.phases ?? [])]
			.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
			.forEach((phase) => {
				const start = addMinutes(orderStart, offset + phase.startOffsetMinutes);
				placed.push({
					phase,
					operation,
					start,
					end: addMinutes(start, phase.durationMinutes),
					workstation,
				});
			});
	});
	return placed;
};

/**
 * Worker-hours an order demands: crew x duration of every placed LABOR phase.
 * Machine phases occupy the unit but need nobody, so they add nothing.
 */
export const orderRequiredWorkerHours = (
	order: MatchableOrder,
	standard: LaborStandard
): number =>
	placeOrderPhases(order, standard).reduce((total, placed) => {
		if (isMachinePhase(placed.phase)) return total;
		return (
			total +
			((placed.end.getTime() - placed.start.getTime()) / 3600000) * placed.phase.workersRequired
		);
	}, 0);
